import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { BRAND } from "@/data/site";
import { ArrowRight, PlayCircle, Video } from "lucide-react";

// Apply Cloudinary auto-optimisation to any Cloudinary URL:
//  f_auto = best format (WebP/AVIF for images, MP4/WebM for video)
//  q_auto = smallest file size at visually lossless quality
//  w_1600 = cap width at 1600px for images (keeps mobile fast)
function optimise(url = "", isVideo = false) {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  const transform = isVideo ? "f_auto,q_auto" : "f_auto,q_auto,w_1600,c_limit";
  return url.replace("/upload/", `/upload/${transform}/`);
}

export default function Gallery() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/gallery?limit=60")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, []);

  // Merge admin-uploaded items with the legacy seed items from BRAND.gallery so
  // the page is never empty before you upload anything.
  const seed = (BRAND.gallery || []).map((g, i) => ({
    id: `seed-${i}`, type: "photo", url: g.url, thumbnail_url: g.url, title: g.title, caption: g.caption,
  }));
  const combined = items === null ? [] : [...items, ...seed];

  return (
    <div data-testid="gallery-page" className="pt-32">
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-12">
        <div className="font-display uppercase tracking-[0.3em] text-xs text-[#8DE038] mb-3">/ Gallery</div>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-7xl text-[#F0F4F1] max-w-4xl">
          Real work by <span className="text-[#8DE038]">Green Connect pros.</span>
        </h1>
        <p className="mt-5 text-[#9CAEA3] max-w-2xl">Before &amp; afters, transformations, timelapses. Fresh work added regularly.</p>
      </section>

      <section className="section-pad bg-[#050806]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {items === null && (
            <div className="text-[#9CAEA3] text-center py-20">Loading…</div>
          )}
          {items !== null && combined.length === 0 && (
            <div data-testid="gallery-empty" className="text-[#9CAEA3] border border-dashed border-[#1F3325] p-16 text-center">
              No transformations posted yet. Check back soon!
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {combined.map((g, i) => (
              <GalleryTile key={g.id || g.url} item={g} feature={i === 0} />
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-5 md:px-8 mt-20 text-center">
          <h3 className="font-display uppercase tracking-tighter text-3xl md:text-5xl text-[#F0F4F1]">Want yours next?</h3>
          <div className="mt-7 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-primary">Book Now <ArrowRight size={18}/></Link>
            <Link to="/register?role=contractor" className="btn-secondary">Join as a Pro</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// Given a Cloudinary video URL, return a JPG poster (frame at 2s)
function videoPoster(url = "") {
  if (!url || !url.includes("res.cloudinary.com")) return url;
  return url.replace("/upload/", "/upload/so_2,f_jpg,q_auto/").replace(/\.[^/.]+$/, ".jpg");
}

function GalleryTile({ item, feature }) {
  const [playing, setPlaying] = useState(false);
  const isVideo = item.type === "video";
  const posterUrl = optimise(item.thumbnail_url || (isVideo ? videoPoster(item.url) : item.url));
  const containerCls = `gl-card overflow-hidden group ${feature ? "lg:col-span-2 lg:row-span-2" : ""}`;
  const mediaCls = `overflow-hidden bg-[#050806] ${feature ? "aspect-[16/9]" : "aspect-[4/3]"}`;

  return (
    <div data-testid={`gallery-tile-${item.id}`} className={containerCls}>
      <div className={mediaCls}>
        {isVideo ? (
          playing ? (
            <video
              data-testid="gallery-video"
              src={optimise(item.url, true)}
              poster={posterUrl}
              controls
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <button
              onClick={() => setPlaying(true)}
              data-testid="gallery-video-play"
              aria-label={`Play ${item.title}`}
              className="relative w-full h-full flex items-center justify-center group-hover:scale-105 transition-transform duration-700"
            >
              <img
                src={posterUrl}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors"/>
              <PlayCircle size={feature ? 88 : 64} className="relative text-white drop-shadow-2xl group-hover:scale-110 transition-transform"/>
              <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#8DE038] text-[#050806] px-2 py-1 font-display uppercase tracking-widest text-[10px]"><Video size={12}/> Video</div>
            </button>
          )
        ) : (
          <img
            src={optimise(item.url)}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        )}
      </div>
      <div className="p-5">
        <div className="font-display uppercase tracking-wider text-xl text-[#F0F4F1]">{item.title}</div>
        {item.caption && <div className="text-[#9CAEA3] text-sm mt-1">{item.caption}</div>}
        {item.category && <div className="inline-block mt-3 px-2 py-1 border border-[#8DE038]/40 text-[#8DE038] font-display uppercase tracking-widest text-[10px]">{item.category}</div>}
      </div>
    </div>
  );
}
